import { Authenticated, Refine } from "@refinedev/core";
import { RefineKbarProvider } from "@refinedev/kbar";
import ru_RU from "antd/es/locale/ru_RU";

import { ThemedLayoutV2, useNotificationProvider } from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";
import "suneditor/dist/css/suneditor.min.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/ru";

import routerProvider, {
  NavigateToResource,
  CatchAllNavigate,
} from "@refinedev/react-router-v6";
import { App as AntdApp, ConfigProvider } from "antd";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { CustomSider } from "./layout/sider";

import { Login } from "./pages/Login/login.page";

import dataProvider from "@refinedev/simple-rest";

import { authProvider, ShowProvider } from "./providers";
import {
  CLUBS_ROUTES,
  clubsAchievementsDataProvider,
  clubsColorDataProvider,
  clubsDataProvider,
  clubsDescriptionDataProvider,
  clubsEmailDataProvider,
  clubsStadiumCapacityDataProvider,
  clubsMediaDataProvider,
  clubsResource,
  clubsTagsDataProvider,
  HASH_TAGS_ROUTES,
  hashTagsDataProvider,
  hashTagsResource,
  MATCHES_ROUTES,
  matchesDataProvider,
  matchesMediaDataProvider,
  matchesDescriptionDataProvider,
  matchesResource,
  NEWS_ROUTES,
  newsDataProvider,
  newsResource,
  seasonsDataProvider,
  seasonsResourceClubsLeagueGenerator,
  seasonsResourceLeagueGenerator,
  toursDataProvider,
  translationsDataProvider,
  socialLinksDataProvider,
  USERS_ROUTES,
  LEAGUES_ROUTES,
  usersDataProvider,
  usersResource,
  leaguesResource,
  aboutResource,
  employeesResource,
  leagueInfoDataProvider,
  groupsDataProvider,
  mappingResource,
  pariResource,
  leonAResource,
  leonBResource,
  MAPPING_ROUTES,
  mappingPariDataProvider,
  mappingLeonADataProvider,
  mappingLeonBDataProvider,
  rusStatResource,
  mappingRusstatDataProvider,
  partnersResource,
  supportMessagesResource,
  usersImportResource,
  USERS_IMPORT_ROUTES,
  pushNotificationResource,
  PUSH_NOTIFICATION_ROUTES,
  pushNotificationDataProvider,
  pushSegmentsDataProvider,
  pushStatsDataProvider,
  segmentsResource,
  segmentUsersResource,
  SEGMENTS_ROUTES,
  usersImportDataProvider,
  PARTNERS_ROUTES,
  SUPPORT_MESSAGES_ROUTES,
  partnersDataProvider,
  whereToLookDataProvider,
  supportMessagesDataProvider,
  rusStatClubsResource,
  rusStatPlayersResource,
  clubsBackgroundDataProvider,
  mappingAutoRusstatPlayersDataProvider,
  whereToLookResource,
  lookResourceLeagueGenerator,
  broadcastExportResource,
  WHERE_TO_LOOK_ROUTES,
  employeesPresidiumResource,
  employeesTeamResource,
  socialResponsibilityResource,
  docsResource,
  presentationsResource,
  philosophyResource,
  partnerResource,
  clubManagementResource,
  investorManagementResource,
  stadiumManagementResource,
  lawyerManagementResource,
  bannersResource,
  videoResource,
  VIDEO_ROUTES,
  videoDataProvider,
  STATS_PUSHES_ROUTES,
  pushAnalyticsResource,
  statsPushesResource,
  CHAMPION_ANIMATION_ROUTES,
  championAnimationDataProvider,
  championAnimationResource,
  APP_ICON_ROUTES,
  appIconDataProvider,
  appIconResource,
  MEDICINE_ROUTES,
  medicineAnnouncementsDataProvider,
  medicineContestsDataProvider,
  medicineDigestsDataProvider,
  medicineSectionDataProvider,
  medicineResource,
  AWARDS_ROUTES,
  awardTypesDataProvider,
  awardsDataProvider,
  playerAwardsDataProvider,
  awardTypesResource,
  awardsResource,
  tvResources,
  TV_ROUTES,
  tvDataProvider,
} from "./pages";
import { RedirectPage } from "./pages/Redirect";
import { accessControlProvider } from "./access/accessControlProvider";
import { SectionGuard } from "./access/SectionGuard";
import { adminAccessResource, ADMIN_ACCESS_ROUTES } from "./pages/AdminAccess";
import { uploadDataProvider } from "./providers/uploadDataProvider";
import { newsPinDataProvider } from "./pages/News/Providers/news-pin-data-provider";
import { mappingRusstatClubsDataProvider } from "./pages/Mapping/Russtat/Providers/mapping-russtat-clubs-data-provider";
import { mappingRusstatPlayersDataProvider } from "./pages/Mapping/Russtat/Providers/mapping-russtat-players-data-provider";
import { employeesPresidiumDataProvider } from "pages/Leagues/Providers/employees-presidium-data-provider";
import { employeesTeamDataProvider } from "pages/Leagues/Providers/employees-team-data-provider";
import { departmentsTeamDataProvider } from "pages/Leagues/Providers/departments-team-data-provider";
import { socialResponsibilityDataProvider } from "pages/Leagues/Providers/social-responsibility-data-provider";
import { docsDataProvider } from "pages/Leagues/Providers/docs-data-provider";
import { presentationsDataProvider } from "pages/Leagues/Providers/presentations-data-provider";
import { philosophyDataProvider } from "pages/Leagues/Providers/philosophy-data-provider";
import { clubManagementDataProvider } from "pages/Leagues/Providers/club-management-data-provider";
import { investorManagementDataProvider } from "pages/Leagues/Providers/investor-management-data-provider";
import { stadiumManagementDataProvider } from "pages/Leagues/Providers/stadium-management-data-provider";
import { lawyerManagementDataProvider } from "pages/Leagues/Providers/lawyer-management-data-provider";
import { partnerDataProvider } from "pages/Leagues/Providers/partner-data-provider";
import { clubsStadiumDataProvider } from "pages/Clubs/Providers/clubs-stadium-data-provider";
import { clubsPhotoStadiumDataProvider } from "pages/Clubs/Providers/clubs-photo-stadium-data-provider";
import { clubsTrophiesDataProvider } from "pages/Clubs/Providers/clubs-trophies-data-provider";
import { clubsMapCoordinatesDataProvider } from "pages/Clubs/Providers/clubs-map-coordinates-data-provider";
import { subsectionsDocsDataProvider } from "pages/Leagues/Providers/subsections-docs-data-provider";
import { commentatorsResource } from "./pages/Commentators/commentators-resource";
import { COMMENTATORS_ROUTES } from "./pages/Commentators";
import { commentatorsDataProvider } from "./pages/Commentators/Providers/commentators-data-provider";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("ru"); // Set Russian locale for dayjs

function App() {
  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <ConfigProvider locale={ru_RU}>
          <AntdApp>
            <Refine
              authProvider={authProvider}
              accessControlProvider={accessControlProvider}
              notificationProvider={useNotificationProvider}
              routerProvider={routerProvider}
              dataProvider={{
                default: dataProvider("https://api.fake-rest.refine.dev"),
                uploadDataProvider: uploadDataProvider(""),
                matchesDataProvider: matchesDataProvider("/cms/calendar"),
                matchesMediaDataProvider: matchesMediaDataProvider(""),
                matchesDescriptionDataProvider:
                  matchesDescriptionDataProvider(""),
                seasonsDataProvider: seasonsDataProvider(""),
                newsDataProvider: newsDataProvider(""),
                newsPinDataProvider: newsPinDataProvider(""),
                usersDataProvider: usersDataProvider(""),
                toursDataProvider: toursDataProvider(""),
                groupsDataProvider: groupsDataProvider(""),
                translationsDataProvider: translationsDataProvider(""),
                socialLinksDataProvider: socialLinksDataProvider(""),
                usersImportDataProvider: usersImportDataProvider(""),
                pushNotificationDataProvider: pushNotificationDataProvider(""),
                pushSegmentsDataProvider: pushSegmentsDataProvider("/api/v1"),
                pushStatsDataProvider: pushStatsDataProvider("/api/v1"),
                clubsDataProvider: clubsDataProvider("/cms/info/teamsBySeason"),
                clubsMediaDataProvider: clubsMediaDataProvider(""),
                clubsColorDataProvider: clubsColorDataProvider(""),
                subsectionsDocsDataProvider: subsectionsDocsDataProvider(""),
                clubsAchievementsDataProvider:
                  clubsAchievementsDataProvider(""),
                employeesPresidiumDataProvider:
                  employeesPresidiumDataProvider(""),
                socialResponsibilityDataProvider:
                  socialResponsibilityDataProvider(""),
                philosophyDataProvider: philosophyDataProvider(""),
                clubManagementDataProvider: clubManagementDataProvider(""),
                investorManagementDataProvider:
                  investorManagementDataProvider(""),
                stadiumManagementDataProvider:
                  stadiumManagementDataProvider(""),
                lawyerManagementDataProvider: lawyerManagementDataProvider(""),
                clubsStadiumDataProvider: clubsStadiumDataProvider(""),
                partnerDataProvider: partnerDataProvider(""),
                docsDataProvider: docsDataProvider(""),
                presentationsDataProvider: presentationsDataProvider(""),
                employeesTeamDataProvider: employeesTeamDataProvider(""),
                clubsPhotoStadiumDataProvider:
                  clubsPhotoStadiumDataProvider(""),
                departmentsTeamDataProvider: departmentsTeamDataProvider(""),
                clubsDescriptionDataProvider: clubsDescriptionDataProvider(""),
                clubsEmailDataProvider: clubsEmailDataProvider(""),
                clubsStadiumCapacityDataProvider:
                  clubsStadiumCapacityDataProvider(""),
                clubsTrophiesDataProvider: clubsTrophiesDataProvider(""),
                clubsMapCoordinatesDataProvider:
                  clubsMapCoordinatesDataProvider(""),
                hashTagsDataProvider: hashTagsDataProvider(""),
                clubsTagsDataProvider: clubsTagsDataProvider(""),
                leagueInfoDataProvider: leagueInfoDataProvider(""),
                mappingPariDataProvider: mappingPariDataProvider(""),
                mappingLeonADataProvider: mappingLeonADataProvider(""),
                mappingLeonBDataProvider: mappingLeonBDataProvider(""),
                mappingRusstatDataProvider: mappingRusstatDataProvider(""),
                mappingRusstatClubsDataProvider:
                  mappingRusstatClubsDataProvider(""),
                mappingRusstatPlayersDataProvider:
                  mappingRusstatPlayersDataProvider(""),
                partnersDataProvider: partnersDataProvider(""),
                commentatorsDataProvider: commentatorsDataProvider(""),
                whereToLookDataProvider: whereToLookDataProvider(""),
                supportMessagesDataProvider: supportMessagesDataProvider(""),
                clubsBackgroundDataProvider: clubsBackgroundDataProvider(""),
                mappingAutoRusstatPlayersDataProvider:
                  mappingAutoRusstatPlayersDataProvider(""),
                videoDataProvider: videoDataProvider(""),
                tvDataProvider: tvDataProvider(""),
                championAnimationDataProvider:
                  championAnimationDataProvider(""),
                appIconDataProvider: appIconDataProvider(""),
                awardTypesDataProvider: awardTypesDataProvider(""),
                awardsDataProvider: awardsDataProvider(""),
                playerAwardsDataProvider: playerAwardsDataProvider(""),
                medicineAnnouncementsDataProvider:
                  medicineAnnouncementsDataProvider(""),
                medicineContestsDataProvider: medicineContestsDataProvider(""),
                medicineDigestsDataProvider: medicineDigestsDataProvider(""),
                medicineSectionDataProvider: medicineSectionDataProvider(""),
              }}
              options={{
                syncWithLocation: true,
                warnWhenUnsavedChanges: true,
                reactQuery: {
                  devtoolConfig: false,
                  clientConfig: {
                    defaultOptions: {
                      queries: {
                        staleTime: Infinity,
                        retry: false,
                        refetchOnWindowFocus: false,
                        keepPreviousData: true,
                      },
                    },
                  },
                },
              }}
              resources={[
                usersResource,
                hashTagsResource,
                commentatorsResource,
                leaguesResource,
                aboutResource,
                employeesResource,
                employeesPresidiumResource,
                employeesTeamResource,
                socialResponsibilityResource,
                docsResource,
                presentationsResource,
                philosophyResource,
                partnerResource,
                clubManagementResource,
                investorManagementResource,
                stadiumManagementResource,
                lawyerManagementResource,
                bannersResource,
                matchesResource,
                seasonsResourceLeagueGenerator("first-league"),
                seasonsResourceLeagueGenerator("second-league"),
                seasonsResourceLeagueGenerator("third-league"),
                newsResource,
                clubsResource,
                seasonsResourceClubsLeagueGenerator("first-league"),
                seasonsResourceClubsLeagueGenerator("second-league"),
                seasonsResourceClubsLeagueGenerator("third-league"),
                mappingResource,
                pariResource,
                leonAResource,
                leonBResource,
                rusStatResource,
                rusStatClubsResource,
                whereToLookResource,
                lookResourceLeagueGenerator("first-league"),
                lookResourceLeagueGenerator("second-league"),
                lookResourceLeagueGenerator("third-league"),
                broadcastExportResource,
                partnersResource,
                statsPushesResource,
                pushAnalyticsResource,
                championAnimationResource,
                appIconResource,
                awardTypesResource,
                awardsResource,
                medicineResource,
                supportMessagesResource,
                usersImportResource,
                pushNotificationResource,
                segmentsResource,
                segmentUsersResource,
                rusStatPlayersResource,
                videoResource,
                ...tvResources,
                adminAccessResource,
              ]}
            >
              <Routes>
                {/*Некий контент*/}
                <Route
                  element={
                    <Authenticated
                      key="authenticated-layout"
                      fallback={<CatchAllNavigate to="/login" />}
                      v3LegacyAuthProviderCompatible
                    >
                      <ThemedLayoutV2 Sider={() => <CustomSider />}>
                        <SectionGuard>
                          <Outlet />
                        </SectionGuard>
                      </ThemedLayoutV2>
                    </Authenticated>
                  }
                >
                  <Route
                    path="/"
                    index
                    element={
                      <ShowProvider>
                        <RedirectPage />
                      </ShowProvider>
                    }
                  />
                  {USERS_ROUTES}
                  {COMMENTATORS_ROUTES}
                  {MATCHES_ROUTES}
                  {NEWS_ROUTES}
                  {CLUBS_ROUTES}
                  {HASH_TAGS_ROUTES}
                  {LEAGUES_ROUTES}
                  {MAPPING_ROUTES}
                  {PARTNERS_ROUTES}
                  {SUPPORT_MESSAGES_ROUTES}
                  {USERS_IMPORT_ROUTES}
                  {PUSH_NOTIFICATION_ROUTES}
                  {SEGMENTS_ROUTES}
                  {WHERE_TO_LOOK_ROUTES}
                  {VIDEO_ROUTES}
                  {TV_ROUTES}
                  {STATS_PUSHES_ROUTES}
                  {CHAMPION_ANIMATION_ROUTES}
                  {APP_ICON_ROUTES}
                  {AWARDS_ROUTES}
                  {MEDICINE_ROUTES}
                  {ADMIN_ACCESS_ROUTES}
                </Route>

                {/*Авторизация*/}
                <Route
                  element={
                    <Authenticated
                      fallback={<Outlet />}
                      v3LegacyAuthProviderCompatible
                    >
                      <NavigateToResource resource="matches" />
                    </Authenticated>
                  }
                >
                  <Route path="/login" element={<Login />} />
                </Route>
              </Routes>
              {/*<UnsavedChangesNotifier*/}
              {/*  message={"Вы уверены что хотите выйти? Есть несохраненные данные"}*/}
              {/*/>*/}
              {/*<DocumentTitleHandler />*/}
            </Refine>
          </AntdApp>
        </ConfigProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
